import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { DEFAULT_CREATE_FORM } from "./goalFormat";

const ratesFrom = (form) => ({
  leadToOpportunity: parseFloat(form.leadToOpportunity),
  opportunityToProposal: parseFloat(form.opportunityToProposal),
  proposalToClose: parseFloat(form.proposalToClose),
  averageDealSize: parseFloat(form.averageDealSize),
});

export const useGoals = (userId, currentInstance) => {
  const [user, setUser] = useState(null);
  const [deltaInfo, setDeltaInfo] = useState(null);
  const [goalHierarchy, setGoalHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM);

  const effectiveData = useMemo(() => {
    const src = currentInstance || user || {};
    return {
      revenueGoal: src.revenueGoal || 0,
      currentRevenue: src.currentRevenue || 0,
      goalEndDate: src.goalEndDate || "",
      companyName: src.companyName || src.name || "",
    };
  }, [currentInstance, user]);

  const instanceId = currentInstance?.instanceId;
  const fetchData = useCallback(async () => {
    try {
      const [userRes, deltaRes, hierarchyRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/agent/goal-delta/${userId}`),
        axios.get(`${API}/goals/${userId}${instanceId ? `?instance_id=${instanceId}` : ""}`),
      ]);
      setUser(userRes.data);
      setDeltaInfo(deltaRes.data);
      setGoalHierarchy(hierarchyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load goals");
    } finally {
      setLoading(false);
    }
  }, [userId, instanceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (effectiveData.revenueGoal) {
      setCreateForm((prev) => ({ ...prev, revenueGoal: effectiveData.revenueGoal, deadline: effectiveData.goalEndDate || prev.deadline }));
    }
  }, [effectiveData.revenueGoal, effectiveData.goalEndDate]);

  const run = async (setBusy, fn, successMsg, errorMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await fetchData();
      return true;
    } catch (error) {
      console.error(errorMsg, error);
      toast.error(errorMsg);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveGoals = (editData) => run(setSaving, () => axios.put(`${API}/users/${userId}`, {
    revenueGoal: parseFloat(editData.revenueGoal),
    currentRevenue: parseFloat(editData.currentRevenue),
    goalEndDate: editData.goalEndDate,
  }), "Goals updated successfully!", "Failed to save goals");

  const createHierarchy = () => run(setSaving, () => axios.post(`${API}/goals/${userId}`, {
    userId,
    revenueGoal: parseFloat(createForm.revenueGoal),
    deadline: createForm.deadline,
    conversionRates: ratesFrom(createForm),
  }), "Goal hierarchy created!", "Failed to create goal hierarchy");

  const recalculate = () => run(setRecalculating, () => axios.post(`${API}/goals/${userId}/recalculate`, ratesFrom(createForm)),
    "Goals recalculated!", "Failed to recalculate");

  return { user, deltaInfo, goalHierarchy, loading, saving, recalculating, effectiveData, createForm, setCreateForm, saveGoals, createHierarchy, recalculate };
};
